import React, { useState, useEffect } from "react";
import "./ShareEnvironmentModal.css";
import { api } from "./api";
import { IconPerson, IconClose } from "./Icons";
import { describeError } from "./errors";
import RoleShareToggle from "./RoleShareToggle";
import UserSearchInput from "./UserSearchInput";

export default function ShareEnvironmentModal({ open, envId, ownerEmail, onClose, onSaved, shareEnvironment }) {
  const [users, setUsers] = useState([]);
  const [userNameMap, setUserNameMap] = useState({});
  const [roles, setRoles] = useState([]);
  const [everyone, setEveryone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load current sharing settings whenever the modal opens
  useEffect(() => {
    if (!open || !envId) return;
    setError(null);
    setLoading(true);
    (async () => {
      try {
        const env = await api.getEnvironment(envId);
        setUsers(env.shared_with_users || []);
        setRoles(env.shared_with_roles || []);
        setEveryone(!!env.shared_with_everyone);
      } catch (e) {
        setError(describeError(e) || "Failed to load sharing settings");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, envId]);

  const addUserFromSearch = (user) => {
    const email = (user.email || "").trim().toLowerCase();
    if (!email) return;
    if (user.firstName || user.lastName) {
      setUserNameMap((prev) => ({ ...prev, [email]: `${user.firstName || ""} ${user.lastName || ""}`.trim() }));
    }
    setUsers((prev) => (prev.includes(email) ? prev : [...prev, email].sort()));
  };

  const removeUser = (email) => {
    setUsers((prev) => prev.filter((u) => u !== email));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await shareEnvironment(envId, {
        sharedWithUsers: users,
        sharedWithRoles: roles,
        sharedWithEveryone: everyone,
      });
      onSaved?.();
      onClose();
    } catch (e) {
      setError(describeError(e) || "Failed to save sharing settings");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="share-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="share-modal">
        <div className="share-header">
          <h2>Share environment</h2>
          <button className="share-close" onClick={onClose} title="Close" aria-label="Close">
            <IconClose size={16} />
          </button>
        </div>

        {error && <div className="share-error">{error}</div>}
        {loading && <div className="share-loading">Loading…</div>}

        <div className="share-section">
          <div className="share-label">Owner</div>
          <div className="share-owner">
            <IconPerson size={14} />
            <span>{ownerEmail || "you"}</span>
            <span className="share-owner-badge">Owner</span>
          </div>
        </div>

        <div className="share-section">
          <div className="share-label">Shared with users</div>
          {users.length === 0 ? (
            <div className="share-empty">No users yet.</div>
          ) : (
            <div className="share-chips">
              {users.map((u) => (
                <span key={u} className="share-chip">
                  {userNameMap[u] || u}
                  <button onClick={() => removeUser(u)} title="Remove" aria-label="Remove user">
                    <IconClose size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <UserSearchInput onSelectUser={addUserFromSearch} />
        </div>

        <div className="share-section">
          <div className="share-label">Shared with roles</div>
          <RoleShareToggle value={roles} onChange={setRoles} />
        </div>

        <div className="share-section">
          <label className="share-everyone">
            <input
              type="checkbox"
              checked={everyone}
              onChange={(e) => setEveryone(e.target.checked)}
            />
            <span>
              <strong>Share with everyone in the org</strong>
              <span className="share-everyone-hint"> — any authenticated user can access</span>
            </span>
          </label>
        </div>

        <div className="share-info">
          When users get access, they can:
          <ul>
            <li>See the same files and skills</li>
            <li>Have their own private chat threads in this env</li>
            <li>Make changes (or have the agent make changes) that everyone sees</li>
          </ul>
          Only you can rename, delete, or change who has access.
        </div>

        <div className="share-actions">
          <button className="ca-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="share-save ca-btn ca-btn--primary" onClick={save} disabled={saving || loading}>
            {saving ? "Saving…" : "Save sharing settings"}
          </button>
        </div>
      </div>
    </div>
  );
}
